import useHasPermissions from '@/components/access-control/useHasPermissions.hook';
import { CardMainActionButtonProps } from '@/components/card/Card.component';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { PlanFunds } from '@/services/Investment.service';
import { ProgramChangeWithApproval } from '@/services/ops/investments/Program.service';
import { PlanService } from '@/services/Plan.service';
import { userService } from '@/services/User.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isAfter, startOfDay, startOfToday } from 'date-fns';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProgramChangeReview } from './ProgramChanges';

type ProgramChangeReviewPageProps = {
  programChange: ProgramChangeWithApproval;
  currentProgram: PlanFunds['currentProgram'];
  newProgram: PlanFunds['newProgram'];
};

export const ProgramChangeReviewPage: FC<
  ProgramChangeReviewPageProps
> = props => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAnotherApprover =
    userService.getUser()?.nickname !==
    props.programChange.approvalCreatedBy.id;

  const hasModifyPermission = useHasPermissions({
    requires: [FeatureLevelPermissions.WRITE_INVESTMENTS_MODIFY]
  });

  const approveOrRejectProgramChange = useMutation(
    [
      'PlanService.putApproveOrRejectProgramChange',
      props.programChange.sponsorPlanId,
      props.programChange.programChangeId
    ],
    (approval: boolean) =>
      PlanService.putApproveOrRejectProgramChange(
        props.programChange.sponsorPlanId,
        props.programChange.programChangeId,
        approval
      ),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: 'Error saving data',
          severity: 'error'
        });
      },
      onSettled: () => {
        navigate(`/ops/investments/program/${props.newProgram.programId}`);
      },
      onSuccess: () => {
        snackbar.showSnackbar({
          message: 'Your data has been saved',
          severity: 'success'
        });
        queryClient.refetchQueries([
          'ProgramService.getProgramChangesWithApprovals',
          props.newProgram.programId
        ]);
      }
    }
  );

  const cancelProgramChange = useMutation(
    [
      'PlanService.postCancelProgramChangeApproval',
      props.programChange.sponsorPlanId,
      props.programChange.programChangeId
    ],
    () =>
      PlanService.postCancelProgramChangeApproval(
        props.programChange.sponsorPlanId,
        props.programChange.programChangeId
      ),
    {
      onError: () => {
        snackbar.showSnackbar({
          message: 'Error saving data',
          severity: 'error'
        });
      },
      onSettled: () => {
        navigate(`/ops/investments/program/${props.newProgram.programId}`);
      },
      onSuccess: () => {
        snackbar.showSnackbar({
          message: 'Your data has been saved',
          severity: 'success'
        });
        queryClient.refetchQueries([
          'ProgramService.getProgramChangesWithApprovals',
          props.newProgram.programId
        ]);
      }
    }
  );

  const confirmButtonProps = useMemo<CardMainActionButtonProps[]>(() => {
    if (
      props.programChange.status === 'pending' &&
      isAnotherApprover &&
      hasModifyPermission.isAllowed
    ) {
      return [
        {
          color: 'error',
          label: 'Reject',
          onClick: async () => {
            await approveOrRejectProgramChange.mutateAsync(false);
          },
          variant: 'text'
        },
        {
          label: 'Approve & Submit',
          onClick: async () => {
            await approveOrRejectProgramChange.mutateAsync(true);
          },
          variant: 'contained'
        }
      ];
    }

    if (
      props.programChange.status === 'approved' &&
      isAfter(startOfDay(props.programChange.effectiveDate), startOfToday()) &&
      hasModifyPermission.isAllowed
    ) {
      return [
        {
          color: 'error',
          label: 'Cancel Change',
          onClick: async () => {
            await cancelProgramChange.mutateAsync();
          },
          variant: 'text'
        }
      ];
    }

    return [];
  }, []);

  return (
    <ProgramChangeReview
      confirmButtonProps={confirmButtonProps}
      currentProgram={props.currentProgram}
      effectiveDate={props.programChange.effectiveDate}
      effectiveDateNote={props.programChange.jiraTicket}
      isConfirming
      mappingButtonProps={[]}
      mappingTableContext={{
        changes: props.programChange.programMappings
      }}
      name={props.programChange.sponsorPlanName}
      newProgram={props.newProgram}
      planId={props.programChange.sponsorPlanId}
    />
  );
};
